import React, { useCallback, useState, useEffect, useRef } from "react"
import { useHistory } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDeviceSize } from "@bappweb/provider"
import Form from "@bappweb/form"
import Panel from "@bappweb/panel"
import TextField from "@bappweb/textfield"
import Button from "@bappweb/button"
import Block from "@bappweb/block"
import Captcha from "@smpu-web/nested/form/captcha"
import FormMessage from "@smpu-web/globalmodules/form-message/index"
import Page from "@smpu-web/globalmodules/page"
import { RegexpTypes, required } from "@bappweb/utils"
import { getDeviceTypeFromSize } from "@smpu-web/utils/utils"
import {
  useGetTokenByTckn1Mutation,
  useGetCaptchaMutation,
  useRefreshCaptchaMutation,
  useStep1Mutation
} from "../../../api/services"
import "../assets/style.scss"
import "../../../../../assets/styles/style.scss"

type PropsType = {
  onError?: (event?: React.BaseSyntheticEvent, errorField?: Record<string, any>) => void
  onSubmit?: (event?: React.BaseSyntheticEvent, values?: Record<string, any>) => void
}

const GUID_MAX_LENGTH = 12
const GUID_MIN_LENGTH = 12
const GUID_REGEX_PATTERN = /([A-Za-z0-9---]+|\d+)$/gi

function MainForm({ onError, onSubmit }: PropsType) {
  const history = useHistory()
  const refForm = useRef(null)

  const size = useDeviceSize()
  const deviceType = getDeviceTypeFromSize(size)

  const { t } = useTranslation()

  const [guid, setGuid] = useState("")
  const [phone, setPhone] = useState("")

  const [captchaValues, setCaptchaValues] = useState({
    tokenSessionId: "",
    captchaData: "",
    captcha: "",
    showCaptcha: false
  })

  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [getTokenByTckn1] = useGetTokenByTckn1Mutation()

  const handleGuidBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const { value } = e.target
      if ((value ?? "").length >= GUID_MIN_LENGTH && value.length <= GUID_MAX_LENGTH && value.match(GUID_REGEX_PATTERN) && captchaValues.tokenSessionId === "") {
        setIsFetching(true)
        getTokenByTckn1(value)
          .unwrap()
          .then((payload) => {
            if (!payload.IsError) {
              setError("")
              setCaptchaValues({
                ...captchaValues,
                tokenSessionId: payload.tokenSessionId
              })
            } else {
              setError(payload.ErrorMessage ? payload.ErrorMessage : "")
            }
          })
          .finally(() => {
            setIsFetching(false)
          })
      }
    },
    [getTokenByTckn1, captchaValues]
  )

  const handleChange = useCallback(
    (e) => {
      const name = e.target.name
      const value = e.target.value

      switch (name) {
        case "guid":
          setError(null)
          setGuid(value)
          setCaptchaValues({
            ...captchaValues,
            captchaData: "",
            tokenSessionId: "",
            showCaptcha: false
          })

          // @ts-ignore TODO
          // Captcha alanı temizlendiğinde validasyon hatası alıyor. O nedenle bu şekilde alanı temizliyoruz.
          refForm.current?.setValue("captcha", "")
          break
        case "phone":
          setError(null)
          setPhone(value)
          break
        case "captcha":
          setError(null)
          setCaptchaValues({
            ...captchaValues,
            captcha: value
          })
          break
        default:
          break
      }
    },
    [captchaValues]
  )

  const [getCaptcha] = useGetCaptchaMutation()

  useEffect(() => {
    if (captchaValues.captchaData === "" && captchaValues.tokenSessionId !== "") {
      setIsFetching(true)
      getCaptcha({
        guid: guid,
        TokenSessionId: captchaValues.tokenSessionId,
        language:t("LangCode")
      })
        .unwrap()
        .then((payload) => {
          if (!payload.IsError) {
            setCaptchaValues({
              ...captchaValues,
              captchaData: payload.captchaData,
              showCaptcha: true
            })
          } else {
            setError(payload.ErrorMessage ? payload.ErrorMessage : "")
          }
        })
        .finally(() => {
          setIsFetching(false)
        })
    }
  }, [guid, getCaptcha, captchaValues, t])

  const [refreshCaptcha] = useRefreshCaptchaMutation()
  const handleCaptchaRefresh = useCallback(() => {
    setIsFetching(true)
    refreshCaptcha({
      guid: guid,
      TokenSessionId: captchaValues.tokenSessionId
    })
      .unwrap()
      .then((payload) => {
        if (!payload.IsError) {
          setCaptchaValues({
            ...captchaValues,
            captchaData: payload.captchaData
          })
        } else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [guid, captchaValues, refreshCaptcha])

  const [step1] = useStep1Mutation()

  const handleSubmit = useCallback(
    (e?: React.BaseSyntheticEvent, values?: Record<string, any>) => {
      if (guid && guid.match(GUID_REGEX_PATTERN) && phone &&  captchaValues.captcha !== "") {
        setIsFetching(true)
        step1({
          guid: guid,
          phoneNumberLast4Digit: phone,
          language: t("LangCode"),
          deviceType: deviceType,
          captcha: captchaValues.captcha.toUpperCase(),
          TokenSessionId: captchaValues.tokenSessionId
        })
          .unwrap()
          .then((payload) => {
            if (!payload.IsError) {
              setError(null)
              history.push({
                pathname: "/details/smsotp",
                state: payload
              })
            } else {
              setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              window.scrollTo({
                top: 0
              })
              refreshCaptcha({
                guid: guid,
                TokenSessionId: captchaValues.tokenSessionId
              })
                .unwrap()
                .then((payload) => {
                  if (!payload.IsError) {
                    setCaptchaValues({
                      ...captchaValues,
                      captcha: "",
                      showCaptcha: true,
                      captchaData: payload.captchaData
                    })
                  } else {
                    setError(payload.ErrorMessage ? payload.ErrorMessage : "")
                  }
                })
                .finally(() => {
                  setIsFetching(false)
                })
            }
          })
          .finally(() => {
            setIsFetching(false)
          })
      }
    },
    [captchaValues, guid, phone, step1, refreshCaptcha, history, t, deviceType]
  )

  return (
    <Page>
      <Block className="text-section">
        {t("Welcome")}
      </Block>
      <Form onSubmit={handleSubmit} onChange={handleChange} grid={false} ref={refForm}>
        <Panel hasFooter connected={false} loading={isFetching}>
          <FormMessage className={"form-msg-error"} error={error}></FormMessage>
          <Panel.Body>
            <TextField
              id="guid"
              type="text"
              name="guid"
              label={t("DocumentNumber")}
              maxLength={12}
              onBlur={handleGuidBlur}
              validations={[required(t("DocumentNumberRequiredError"))]}
            />
            <TextField
              id="phone"
              type="text"
              name="phone"
              label={t("Last4DigitOfYourNumber")}
              maxLength={4}
              validator={RegexpTypes.Numeric}
              validations={[required(t("Last4DigitOfYourNumberReuiredError"))]}
            />
            {captchaValues.showCaptcha && (
              <Captcha
                label={t("SecurityCode_UpperCase")}
                id="captcha"
                type="text"
                name="captcha"
                captcha={captchaValues.captchaData}
                value={captchaValues.captcha}
                maxLength={4}
                maxRefreshCount={3}
                refreshClick={handleCaptchaRefresh}
                validations={[required(t("SecurityCodeRequiredError"))]}
              />
            )}
          </Panel.Body>
          <Panel.Footer>
            <Panel.RightFooter>
              <Button text={t("Continue_UpperCase")} type="submit" disabled={isFetching} />
            </Panel.RightFooter>
          </Panel.Footer>
        </Panel>
      </Form>
    </Page>
  )
}

export default MainForm
